import React, { ReactNode } from 'react';
import classNames from 'classnames';
import Box from '../Box';
import ButtonBase from '../ButtonBase';
import Text from '../Text';
import IconMore16 from '../icons/IconMore16';
import IconMore24 from '../icons/IconMore24';
import IconLess16 from '../icons/IconLess16';
import IconLess24 from '../icons/IconLess24';
import { useControlled } from '../_internal/hooks';
import { MarginProps } from '../_internal/spacing';
import useUniqueId from '../_internal/useUniqueId';
import { getResponsiveValue, ResponsiveValue } from '../_internal/styling';
import useScreenSize from '../_internal/useScreenSize';
import AccordionTransition from './AccordionTransition';

import styles from './accordion.module.scss';

interface Props {
  /**
   * The accordion panel content that is revealed when the accordion is expanded
   */
  children: ReactNode;

  /**
   * The location of the icon that visually communicates the expanded state.
   * This value is responsive and can be configured differently for multiple
   * screen sizes.
   *
   * - `"appended"` - The icon is displayed at the end of the text (used for an accordion by itself)
   * - `"fixed-end"` - The icon is fixed to the end of the line leaving whitespace in between it and the text (used for multiple stacked accordions)
   *
   * @default 'appended'
   */
  expandIconAlignment?: ResponsiveValue<'appended' | 'fixed-end'>;

  /**
   * The accordion heading
   */
  heading: ReactNode;

  /**
   * The level of the accordion heading, controlling which HTML heading
   * element is rendered
   */
  headingLevel: 'none' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  /**
   * The font size of the accordion heading
   *
   * @default "title-xsmall"
   */
  headingSize?: 'title-xsmall' | 'title-small' | 'title-medium';

  /**
   * The color of the accordion heading
   *
   * @default "gray"
   */
  color?: 'gray' | 'navy';

  /**
   * Unique ID of the accordion element that is used for accessibility to
   * associate the heading and panel elements. Defaults to an auto-generated ID
   * when unspecified.
   */
  id?: string;

  /**
   * Callback fired when the expand/collapse state is changed
   */
  onChange?: (newIsExpanded: boolean) => void;
}

interface ControlledProps {
  /**
   * When `true`, expands the accordion. Otherwise it's collapsed. Setting
   * the `isExpanded` prop enables control over the accordion.
   */
  isExpanded: boolean;

  defaultIsExpanded?: undefined;
}
interface UncontrolledProps {
  /**
   * When `true`, expands the accordion by default. The accordion maintains
   * its expanded state internally based on user action.
   *
   * @default false
   */
  defaultIsExpanded?: boolean;

  isExpanded?: undefined;
}

type ControllingProps = ControlledProps | UncontrolledProps;

export type AccordionProps = Props & ControllingProps & MarginProps;

/**
 * An accordion is a lightweight container with a heading that can be expanded to reveal additional content.
 */
const Accordion = ({
  children,
  defaultIsExpanded = false,
  expandIconAlignment = 'appended',
  isExpanded,
  heading,
  headingLevel,
  headingSize = 'title-xsmall',
  color = 'gray',
  id: initialId,
  m,
  mt,
  mb,
  my,
  ml,
  mr,
  mx,
  onChange,
}: AccordionProps) => {
  const screenSize = useScreenSize();
  const [expanded, setExpanded] = useControlled({
    controlledValue: isExpanded,
    defaultValue: defaultIsExpanded,
    name: 'Accordion.isExpanded',
  });
  const uniqueId = useUniqueId();
  const id = initialId || uniqueId;
  const panelId = `${id}-panel`;
  const toggleId = `${id}-toggle`;
  const HeadingContainer =
    !headingLevel || headingLevel === 'none' ? 'div' : headingLevel;
  const margin = { m, mt, mb, my, ml, mr, mx } as MarginProps;
  const iconAlignment = getResponsiveValue(
    screenSize,
    expandIconAlignment,
    'appended',
  );

  const MoreIcon = headingSize === 'title-medium' ? IconMore24 : IconMore16;
  const LessIcon = headingSize === 'title-medium' ? IconLess24 : IconLess16;

  const handleToggle = () => {
    setExpanded(!expanded);
    onChange?.(!expanded);
  };

  return (
    <Box
      id={id}
      {...margin}
      className={classNames(styles[`icon-${iconAlignment}`], {
        [styles.expanded]: expanded,
      })}
    >
      <HeadingContainer>
        <ButtonBase
          className={classNames(styles.toggle, styles[color])}
          id={toggleId}
          aria-controls={panelId}
          aria-expanded={expanded}
          onClick={handleToggle}
        >
          <Box className={styles.heading}>
            <Text as="span" setting={headingSize} mr={0.5}>
              {heading}
            </Text>
            {expanded ? (
              <LessIcon
                color="inherit"
                purpose="decorative"
                className={styles.icon}
              />
            ) : (
              <MoreIcon
                color="inherit"
                purpose="decorative"
                className={styles.icon}
              />
            )}
          </Box>
        </ButtonBase>
      </HeadingContainer>
      <AccordionTransition isExpanded={expanded}>
        <Box id={panelId} role="region" aria-labelledby={toggleId} mt={0.5}>
          {children}
        </Box>
      </AccordionTransition>
    </Box>
  );
};

export default Accordion;
